import type { AdminTenantView } from "@apsu/shared";
import { useState } from "react";
import { Link } from "react-router";
import { ConfirmDeleteDialog } from "../../components/ConfirmDeleteDialog";
import { useFetch } from "../../hooks/useFetch";

type ListResponse = {
	data: AdminTenantView[];
	pagination: {
		page: number;
		pageSize: number;
		total: number;
		totalPages: number;
	};
};

export function AdminTenantsPage() {
	const [page, setPage] = useState(1);
	const { data, loading, error, refetch } = useFetch<ListResponse>(
		`/api/admin/tenants?page=${page}&pageSize=20`,
	);
	const [actionLoading, setActionLoading] = useState<string | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<AdminTenantView | null>(
		null,
	);

	async function handleToggleStatus(tenant: AdminTenantView) {
		const action = tenant.status === "active" ? "suspend" : "activate";
		setActionLoading(tenant.slug);
		try {
			await fetch(`/api/admin/tenants/${tenant.slug}/${action}`, {
				method: "POST",
				credentials: "include",
			});
			refetch();
		} finally {
			setActionLoading(null);
		}
	}

	return (
		<div>
			<h1 className="text-2xl font-bold text-gray-900 mb-4">Tenants</h1>

			{error && <p className="text-red-600 text-sm mb-3">{error}</p>}

			{loading && !data && <p className="text-gray-500 text-sm">Loading…</p>}

			{data && (
				<>
					<div className="overflow-x-auto">
						<table className="w-full text-sm text-left">
							<thead className="bg-gray-100 text-gray-700">
								<tr>
									<th className="px-4 py-2">Name</th>
									<th className="px-4 py-2">Slug</th>
									<th className="px-4 py-2">Status</th>
									<th className="px-4 py-2">Created</th>
									<th className="px-4 py-2">Actions</th>
								</tr>
							</thead>
							<tbody>
								{data.data.map((tenant) => (
									<tr key={tenant.id} className="border-b border-gray-200">
										<td className="px-4 py-2 font-medium">
											<Link
												to={`/admin/tenants/${tenant.slug}`}
												className="text-blue-600 hover:underline"
											>
												{tenant.name}
											</Link>
										</td>
										<td className="px-4 py-2 text-gray-600">{tenant.slug}</td>
										<td className="px-4 py-2">
											<span
												className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
													tenant.status === "active"
														? "bg-green-100 text-green-700"
														: "bg-red-100 text-red-700"
												}`}
											>
												{tenant.status}
											</span>
										</td>
										<td className="px-4 py-2 text-gray-600">
											{new Date(tenant.createdAt).toLocaleDateString()}
										</td>
										<td className="px-4 py-2 flex gap-2">
											<button
												type="button"
												disabled={actionLoading === tenant.slug}
												onClick={() => handleToggleStatus(tenant)}
												className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
											>
												{tenant.status === "active" ? "Suspend" : "Activate"}
											</button>
											<button
												type="button"
												onClick={() => setDeleteTarget(tenant)}
												className="text-xs px-2 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50"
											>
												Delete
											</button>
										</td>
									</tr>
								))}
								{data.data.length === 0 && (
									<tr>
										<td
											colSpan={5}
											className="px-4 py-8 text-center text-gray-500"
										>
											No tenants found.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>

					<div className="flex items-center justify-between mt-4">
						<button
							type="button"
							disabled={page <= 1}
							onClick={() => setPage((p) => p - 1)}
							className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
						>
							Previous
						</button>
						<span className="text-sm text-gray-600">
							Page {data.pagination.page} of {data.pagination.totalPages || 1}
						</span>
						<button
							type="button"
							disabled={page >= data.pagination.totalPages}
							onClick={() => setPage((p) => p + 1)}
							className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
						>
							Next
						</button>
					</div>
				</>
			)}

			{deleteTarget && (
				<ConfirmDeleteDialog
					open={!!deleteTarget}
					tenantName={deleteTarget.name}
					tenantSlug={deleteTarget.slug}
					onClose={() => setDeleteTarget(null)}
					onDeleted={() => {
						setDeleteTarget(null);
						refetch();
					}}
				/>
			)}
		</div>
	);
}
